import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationCity as EstacionIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Public as PaisIcon,
  Flight as FlightIcon,
} from '@mui/icons-material';

const Estaciones = () => {
  const [estaciones, setEstaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedEstacion, setSelectedEstacion] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    Pais: '',
    Nombre: '',
  });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [estacionToDelete, setEstacionToDelete] = useState(null);

  useEffect(() => {
    fetchEstaciones();
  }, []);

  const fetchEstaciones = async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoints.estaciones);
      setEstaciones(res.data.data?.data || res.data.data || []);
    } catch (error) {
      setError('Error al cargar estaciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, estacion = null) => {
    setDialogMode(mode);
    setSelectedEstacion(estacion);
    if (estacion) {
      setFormData({
        code: estacion.code || '',
        Pais: estacion.Pais || '',
        Nombre: estacion.Nombre || '',
      });
    } else {
      setFormData({ code: '', Pais: '', Nombre: '' });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEstacion(null);
    setFormData({ code: '', Pais: '', Nombre: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'code' ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.Pais || !formData.Nombre) {
      setError('Todos los campos son requeridos');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (dialogMode === 'create') {
        await api.post(endpoints.estaciones, formData);
        setSuccess('Estación creada exitosamente');
      } else {
        await api.put(endpoints.estaciones, {
          id: selectedEstacion.id,
          ...formData,
        });
        setSuccess('Estación actualizada exitosamente');
      }

      handleCloseDialog();
      fetchEstaciones();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar estación');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (estacion) => {
    setEstacionToDelete(estacion);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!estacionToDelete) return;

    try {
      setSaving(true);
      await api.delete(endpoints.estaciones, { data: { id: estacionToDelete.id } });
      setSuccess('Estación eliminada exitosamente');
      setDeleteDialogOpen(false);
      setEstacionToDelete(null);
      fetchEstaciones();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar estación');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar estaciones
  const filteredEstaciones = estaciones.filter(
    (e) =>
      e.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.Pais?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.Nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Agrupar por país para estadísticas
  const paisStats = estaciones.reduce((acc, e) => {
    acc[e.Pais] = (acc[e.Pais] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #5E35B1 0%, #7C4DFF 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EstacionIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Estaciones
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  Gestión de aeropuertos y códigos IATA
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component={Link}
                to="/aerolinea"
                startIcon={<BackIcon />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                variant="outlined"
              >
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('create')}
                sx={{ bgcolor: 'white', color: '#5E35B1', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              >
                Nueva Estación
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <FlightIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {estaciones.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Estaciones
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.50' }}>
            <PaisIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="secondary.main">
              {Object.keys(paisStats).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Países
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por código, país o nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Código IATA</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>País</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nombre / Ciudad</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEstaciones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchTerm ? 'No se encontraron resultados' : 'No hay estaciones registradas'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEstaciones.map((estacion) => (
                  <TableRow key={estacion.id} hover>
                    <TableCell>
                      <Chip
                        label={estacion.code}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 'bold', minWidth: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaisIcon fontSize="small" color="action" />
                        {estacion.Pais}
                      </Box>
                    </TableCell>
                    <TableCell>{estacion.Nombre}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog('edit', estacion)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(estacion)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EstacionIcon />
            {dialogMode === 'create' ? 'Nueva Estación' : 'Editar Estación'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Código IATA"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                placeholder="Ej: MIA"
                inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                required
                helperText="3 letras"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="País"
                name="Pais"
                value={formData.Pais}
                onChange={handleInputChange}
                placeholder="Ej: Estados Unidos"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre / Ciudad"
                name="Nombre"
                value={formData.Nombre}
                onChange={handleInputChange}
                placeholder="Ej: Miami International Airport"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {dialogMode === 'create' ? 'Crear' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar la estación{' '}
            <strong>{estacionToDelete?.code}</strong> ({estacionToDelete?.Nombre})?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. Asegúrese de que no haya rutas o vuelos asociados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Estaciones;




